'use strict';

const {
  format: formatDateFns,
  parseISO,
  isValid,
  setDate,
  getDay: getDayDateFns,
  setDay: setDayDateFns,
  addDays: addDaysDateFns,
  subDays: subDaysDateFns,
  addWeeks,
  subWeeks,
  getMonth: getMonthDateFns,
  setMonth: setMonthDateFns,
  addMonths: addMonthsDateFns,
  subMonths: subMonthsDateFns,
  getDaysInMonth: getDaysInMonthDateFns,
  getYear: getYearDateFns,
  setYear: setYearDateFns,
  addYears: addYearsDateFns,
  subYears: subYearsDateFns,
  isBefore,
  isSameDay,
  isAfter,
  isWeekend,
  startOfDay,
  endOfDay,
  lastDayOfMonth,
  differenceInCalendarDays,
  differenceInDays,
  startOfMonth
} = require('date-fns');
const {
  enGB
} = require('date-fns/locale');
const parse = date => typeof date === 'string' ? parseISO(date) : date;
const format = (date, formatString) => formatDateFns(parse(date), formatString, {
  locale: enGB
});
const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dateFromDatetime = datetime => format(datetime, 'yyyy-MM-dd');
const newUtcDate = (year, month, day) => new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
const getDay = date => getDayDateFns(parse(date));
const setDay = (date, dayIndex) => setDayDateFns(parse(date), dayIndex);
const addDays = (date, n) => addDaysDateFns(parse(date), n);
const subDays = (date, n) => subDaysDateFns(parse(date), n);
const getMonth = date => getMonthDateFns(parse(date));
const getMonthString = dateString => {
  const [day, month, year] = dateString.split('/');
  return format(dateFromDatetime(newUtcDate(year, month, day)), 'MMM');
};
const getMonthStringFromISO = dateString => {
  return format(parse(dateString), 'MMM');
};
const setMonth = (date, index) => setMonthDateFns(parse(date), index);
const addMonths = (date, amount) => addMonthsDateFns(parse(date), amount);
const subMonths = (date, amount) => subMonthsDateFns(parse(date), amount);
const getDaysInMonth = date => getDaysInMonthDateFns(parse(date));
const getYear = date => getYearDateFns(parse(date));
const setYear = (date, index) => setYearDateFns(parse(date), index);
const addYears = (date, amount) => addYearsDateFns(parse(date), amount);
const subYears = (date, amount) => subYearsDateFns(parse(date), amount);
const dateIsBefore = (date, referenceDate) => isBefore(parse(date), parse(referenceDate));
const dateIsSame = (date, referenceDate) => isSameDay(parse(date), parse(referenceDate));
const dateIsAfter = (date, referenceDate) => isAfter(parse(date), parse(referenceDate));
const dateIsBetween = (date, firstReferenceDate, secondReferenceDate) => !dateIsBefore(date, firstReferenceDate) && !dateIsAfter(date, secondReferenceDate);
const dateIsWeekend = date => isWeekend(parse(date));
const dayIsWeekday = day => days.slice(1, 6).includes(day);
const ukDateFromDatetime = datetime => format(datetime, 'dd/MM/yyyy');
const icalDateTimeFromDatetime = datetime => format(datetime, "yyyyMMdd'T'HHmmss'Z'");
const timestampFromDate = date => format(date, 'X');
const timeFromDate = date => format(date, 'HH:mm:ss');
const getStartOfDate = date => startOfDay(parse(date));
const getEndOfDate = date => endOfDay(parse(date));
const getTodaysDate = () => dateFromDatetime(Date.now());
const getYesterdaysDate = () => dateFromDatetime(subDays(Date.now(), 1));
const getTomorrowsDate = () => dateFromDatetime(addDays(Date.now(), 1));
const getFirstDateOfPreviousMonth = () => dateFromDatetime(subMonths(setDate(Date.now(), 1), 1));
const getFirstDateOfCurrentMonth = () => dateFromDatetime(setDate(Date.now(), 1));
const getLastDateOfCurrentMonth = () => dateFromDatetime(lastDayOfMonth(Date.now()));
const getFirstDateOfNextMonth = () => dateFromDatetime(addMonths(setDate(Date.now(), 1), 1));
const getLastDateOfNextMonth = () => dateFromDatetime(lastDayOfMonth(addMonths(setDate(Date.now(), 1), 1)));
const getMonthYearStringFromDate = date => format(date, 'MMMM yyyy');
const getCurrentMonthYearString = () => getMonthYearStringFromDate(Date.now());
const getDayStringFromDate = date => days[new Date(date).getDay()];
const getDateString = date => format(date, 'EEEE do MMMM');
const getShortDateString = date => format(date, 'EEE do MMM');
const getDateStringWithYear = date => format(date, "EEEE',' do MMM yyyy");
const getDateFromUkDateString = dateString => {
  const [day, month, year] = dateString.split('/');
  return dateFromDatetime(newUtcDate(year, month, day));
};
const ukDateStringIsValid = dateString => {
  const [day, month, year] = dateString.split('/');
  const date = newUtcDate(year, month, day);
  return isValid(date);
};
const addWeeksAndFormat = (date, n) => dateFromDatetime(addWeeks(parse(date), n));
const subWeeksAndFormat = (date, n) => dateFromDatetime(subWeeks(parse(date), n));
const getDayBeforeDate = (day, date) => {
  const parsedDate = parse(date);
  const occurenceOfDayThisWeek = setDay(parsedDate, days.indexOf(day));
  const previousOccurence = !dateIsBefore(occurenceOfDayThisWeek, parsedDate) ? subWeeks(occurenceOfDayThisWeek, 1) : occurenceOfDayThisWeek;
  return dateFromDatetime(previousOccurence);
};
const getNextBusinessDay = function (initialDate) {
  let holidays = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  let date = parse(initialDate);
  const isHoliday = day => holidays.some(holiday => dateIsSame(holiday, day));
  while (isWeekend(date) || isHoliday(date)) {
    date = addDays(date, 1);
  }
  return dateFromDatetime(date);
};
const getNthFutureBusinessDay = _ref => {
  let {
    n,
    initialDate = getTodaysDate(),
    holidays = []
  } = _ref;
  const isHoliday = day => holidays.some(holiday => dateIsSame(holiday, day));
  let numBusinessDaysAhead = 0;
  let date = parse(initialDate);
  while (numBusinessDaysAhead < n) {
    date = addDays(date, 1);
    if (!isWeekend(date) && !isHoliday(date)) {
      numBusinessDaysAhead += 1;
    }
  }
  return dateFromDatetime(date);
};
const isWithinNBusinessDays = _ref2 => {
  let {
    n,
    date,
    holidays = []
  } = _ref2;
  const futureBusinessDay = getNthFutureBusinessDay({
    n,
    holidays
  });
  return dateIsBefore(date, futureBusinessDay);
};
const isWithinNCalendarDays = _ref3 => {
  let {
    n,
    date
  } = _ref3;
  return differenceInCalendarDays(parse(date), parse(getTodaysDate())) < n;
};
const getDaysUntil = date => {
  const today = getTodaysDate();
  return differenceInCalendarDays(parse(date), parse(today));
};
const getAcademicYearFromDate = date => {
  const parsedDate = parse(date);
  const augustIndex = 7;
  const currentYear = getYear(parsedDate);
  return getMonth(parsedDate) > augustIndex ? currentYear : currentYear - 1;
};
const getStartOfAcademicYearFromDate = date => {
  const year = getAcademicYearFromDate(date);
  return new Date(`${year}-09-01T00:00:00`);
};
const getEndOfAcademicYearFromDate = date => {
  const year = getAcademicYearFromDate(date);
  return new Date(`${year + 1}-08-31T23:59:59`);
};
const getStartOfCurrentAcademicYear = () => getStartOfAcademicYearFromDate(Date.now());
const getEndOfCurrentAcademicYear = () => getEndOfAcademicYearFromDate(Date.now());
const getStartOfNextAcademicYear = () => {
  const startOfCurrentYear = getStartOfCurrentAcademicYear();
  return addYears(startOfCurrentYear, 1);
};
const getEndOfNextAcademicYear = () => {
  const endOfCurrentYear = getEndOfCurrentAcademicYear();
  return addYears(endOfCurrentYear, 1);
};
const getStartDateOfAcademicYear = startYear => `${startYear}-09-01`;
const getEndDateOfAcademicYear = startYear => `${startYear + 1}-08-31`;
const checkTodayDateAfterGiven = date => {
  const [year, month, day] = date.split('-');
  const firstDateOfBookingMonth = startOfMonth(new Date(year, month - 1, day));
  const currentDate = getTodaysDate();
  return dateIsAfter(currentDate, firstDateOfBookingMonth);
};
module.exports = {
  parseDate: parse,
  format,
  dateFromDatetime,
  ukDateFromDatetime,
  icalDateTimeFromDatetime,
  timestampFromDate,
  timeFromDate,
  getDateFromUkDateString,
  ukDateStringIsValid,
  getTodaysDate,
  getYesterdaysDate,
  getTomorrowsDate,
  getDayStringFromDate,
  getDateString,
  getShortDateString,
  getDateStringWithYear,
  getDayBeforeDate,
  getNextBusinessDay,
  getNthFutureBusinessDay,
  getDaysUntil,
  getStartOfDate,
  getEndOfDate,
  getFirstDateOfPreviousMonth,
  getFirstDateOfCurrentMonth,
  getLastDateOfCurrentMonth,
  getCurrentMonthYearString,
  getMonthYearStringFromDate,
  getAcademicYearFromDate,
  getStartOfAcademicYearFromDate,
  getEndOfAcademicYearFromDate,
  getStartOfCurrentAcademicYear,
  getEndOfCurrentAcademicYear,
  getStartOfNextAcademicYear,
  getEndOfNextAcademicYear,
  getStartDateOfAcademicYear,
  getEndDateOfAcademicYear,
  getDay,
  setDay,
  addDays,
  subDays,
  addWeeks: addWeeksAndFormat,
  subWeeks: subWeeksAndFormat,
  getMonth,
  getMonthString,
  getMonthStringFromISO,
  setMonth,
  addMonths,
  subMonths,
  getDaysInMonth,
  getYear,
  setYear,
  addYears,
  subYears,
  dateIsBefore,
  dateIsSame,
  dateIsAfter,
  dateIsBetween,
  dateIsWeekend,
  dayIsWeekday,
  isWithinNBusinessDays,
  isWithinNCalendarDays,
  newUtcDate,
  getFirstDateOfNextMonth,
  getLastDateOfNextMonth,
  checkTodayDateAfterGiven,
  differenceInDays
};